/**
 * FilterNodes Component
 * Recursive component for rendering filter nodes (both field filters and logic groups)
 * Following the reference pattern from nodes.vue
 */

import { useCallback, useMemo } from 'react';
import { Plus, X, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FilterNodeInput } from './FilterNodeInput';
import type { Field } from '@/hooks/useFields';
import { cn } from '@/lib/utils';
import { getDefaultOperator } from '@/utils/filter-operators';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Filter type - can be a field filter or a logic group
export type Filter = {
    _and?: Filter[];
    _or?: Filter[];
    [key: string]: unknown;
};

interface FilterNodesProps {
    filters: Filter[];
    fields: Field[];
    onChange: (filters: Filter[]) => void;
    depth: number;
    disabled?: boolean;
}

interface FilterInfo {
    name: string;
    isField: boolean;
    filter: Filter;
}

/**
 * Get the name (first key) of a filter node
 */
function getNodeName(node: Filter): string {
    if (!node || typeof node !== 'object') return '';
    return Object.keys(node)[0] || '';
}

/**
 * Check if a node is a field filter (not a logic group)
 */
function isFieldFilter(node: Filter): boolean {
    const name = getNodeName(node);
    return name !== '' && !name.startsWith('_');
}

/**
 * Convert field + operator + value to filter format
 */
function fieldToFilter(field: string, operator: string, value: unknown): Filter {
    const sections = field.split('.');

    function buildNested(parts: string[]): Record<string, unknown> {
        const part = parts.shift();
        if (part !== undefined) {
            return { [part]: buildNested(parts) };
        } else {
            return { [operator]: value };
        }
    }

    return buildNested(sections);
}

/**
 * Sortable filter item wrapper
 */
function SortableFilterItem({
    id,
    children,
    disabled,
}: {
    id: string;
    children: React.ReactNode;
    disabled?: boolean;
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id, disabled });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div ref={setNodeRef} style={style} className="flex items-start gap-1">
            <div
                {...attributes}
                {...listeners}
                className={cn(
                    'flex items-center justify-center h-8 w-5 cursor-grab text-muted-foreground hover:text-foreground',
                    disabled && 'cursor-not-allowed opacity-50'
                )}
            >
                <GripVertical className="h-4 w-4" />
            </div>
            <div className="flex-1">{children}</div>
        </div>
    );
}

export function FilterNodes({
    filters,
    fields,
    onChange,
    depth,
    disabled = false,
}: FilterNodesProps) {
    // Sensors for drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Generate stable IDs for drag and drop
    const filterIds = useMemo(() => {
        return filters.map((_, index) => `filter-${depth}-${index}`);
    }, [filters, depth]);

    // Build filter info for each node
    const filterInfo = useMemo((): FilterInfo[] => {
        return filters.map((filter) => {
            const name = getNodeName(filter);
            return {
                name,
                isField: isFieldFilter(filter),
                filter,
            };
        });
    }, [filters]);

    // Handle drag end
    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = filterIds.indexOf(active.id as string);
            const newIndex = filterIds.indexOf(over.id as string);

            if (oldIndex !== -1 && newIndex !== -1) {
                onChange(arrayMove(filters, oldIndex, newIndex));
            }
        }
    }, [filters, filterIds, onChange]);

    // Remove a filter at index
    const removeNode = useCallback((index: number) => {
        const newFilters = [...filters];
        newFilters.splice(index, 1);
        onChange(newFilters);
    }, [filters, onChange]);

    // Update a filter at index
    const updateNode = useCallback((index: number, newFilter: Filter) => {
        const newFilters = [...filters];
        newFilters[index] = newFilter;
        onChange(newFilters);
    }, [filters, onChange]);

    // Toggle logic operator (_and <-> _or)
    const toggleLogic = useCallback((index: number) => {
        const filter = filters[index];
        if (!filter) return;

        const name = getNodeName(filter);

        if (name === '_and' && Array.isArray(filter._and)) {
            updateNode(index, { _or: filter._and });
        } else if (name === '_or' && Array.isArray(filter._or)) {
            updateNode(index, { _and: filter._or });
        }
    }, [filters, updateNode]);

    // Add filter to a group
    const addFilterToGroup = useCallback((index: number) => {
        const filter = filters[index];
        if (!filter) return;

        const name = getNodeName(filter);

        if (fields.length === 0) return;

        const firstField = fields[0];
        if (!firstField) return;

        const operator = `_${getDefaultOperator(firstField.type || 'string')}`;
        const newFilter = fieldToFilter(firstField.field, operator, null);

        if (name === '_and' && Array.isArray(filter._and)) {
            updateNode(index, { _and: [...filter._and, newFilter] });
        } else if (name === '_or' && Array.isArray(filter._or)) {
            updateNode(index, { _or: [...filter._or, newFilter] });
        }
    }, [filters, fields, updateNode]);

    // Add AND group to a group
    const addAndToGroup = useCallback((index: number) => {
        const filter = filters[index];
        if (!filter) return;

        const name = getNodeName(filter);

        if (name === '_and' && Array.isArray(filter._and)) {
            updateNode(index, { _and: [...filter._and, { _and: [] }] });
        } else if (name === '_or' && Array.isArray(filter._or)) {
            updateNode(index, { _or: [...filter._or, { _and: [] }] });
        }
    }, [filters, updateNode]);

    // Add OR group to a group
    const addOrToGroup = useCallback((index: number) => {
        const filter = filters[index];
        if (!filter) return;

        const name = getNodeName(filter);

        if (name === '_and' && Array.isArray(filter._and)) {
            updateNode(index, { _and: [...filter._and, { _or: [] }] });
        } else if (name === '_or' && Array.isArray(filter._or)) {
            updateNode(index, { _or: [...filter._or, { _or: [] }] });
        }
    }, [filters, updateNode]);

    // Handle nested group changes
    const handleNestedChange = useCallback((index: number, newChildren: Filter[]) => {
        const filter = filters[index];
        if (!filter) return;

        const name = getNodeName(filter);

        if (name === '_and') {
            updateNode(index, { _and: newChildren });
        } else if (name === '_or') {
            updateNode(index, { _or: newChildren });
        }
    }, [filters, updateNode]);

    if (filters.length === 0) {
        return null;
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <SortableContext items={filterIds} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                    {filterInfo.map((info, index) => {
                        const itemId = filterIds[index];
                        if (!itemId) return null;

                        return (
                            <SortableFilterItem
                                key={itemId}
                                id={itemId}
                                disabled={disabled}
                            >
                                {info.isField ? (
                                    // Field filter
                                    <div className="flex items-center gap-2">
                                        <FilterNodeInput
                                            filter={info.filter}
                                            fields={fields}
                                            onChange={(newFilter: Filter) => updateNode(index, newFilter)}
                                            disabled={disabled}
                                        />
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                                            onClick={() => removeNode(index)}
                                            disabled={disabled}
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ) : (
                                    // Logic group (_and or _or)
                                    <div
                                        className={cn(
                                            'rounded-lg border p-3',
                                            info.name === '_and'
                                                ? 'border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20'
                                                : 'border-orange-200 bg-orange-50/50 dark:border-orange-900 dark:bg-orange-950/20'
                                        )}
                                    >
                                        {/* Group header */}
                                        <div className="flex items-center justify-between mb-2">
                                            <Badge
                                                variant="outline"
                                                className={cn(
                                                    'cursor-pointer select-none',
                                                    info.name === '_and'
                                                        ? 'border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900'
                                                        : 'border-orange-300 text-orange-700 hover:bg-orange-100 dark:border-orange-800 dark:text-orange-400 dark:hover:bg-orange-900'
                                                )}
                                                onClick={() => !disabled && toggleLogic(index)}
                                            >
                                                {info.name === '_and' ? 'AND' : 'OR'}
                                            </Badge>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                                                onClick={() => removeNode(index)}
                                                disabled={disabled}
                                            >
                                                <X className="h-3 w-3" />
                                            </Button>
                                        </div>

                                        {/* Nested filters */}
                                        <FilterNodes
                                            filters={
                                                info.name === '_and'
                                                    ? (info.filter._and as Filter[]) || []
                                                    : (info.filter._or as Filter[]) || []
                                            }
                                            fields={fields}
                                            onChange={(newChildren) => handleNestedChange(index, newChildren)}
                                            depth={depth + 1}
                                            disabled={disabled}
                                        />

                                        {/* Add buttons for group */}
                                        <div className="flex items-center gap-2 mt-2">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 text-xs"
                                                onClick={() => addFilterToGroup(index)}
                                                disabled={disabled || fields.length === 0}
                                            >
                                                <Plus className="h-3 w-3 mr-1" />
                                                Filter
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 text-xs"
                                                onClick={() => addAndToGroup(index)}
                                                disabled={disabled}
                                            >
                                                <Plus className="h-3 w-3 mr-1" />
                                                AND
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 text-xs"
                                                onClick={() => addOrToGroup(index)}
                                                disabled={disabled}
                                            >
                                                <Plus className="h-3 w-3 mr-1" />
                                                OR
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </SortableFilterItem>
                        );
                    })}
                </div>
            </SortableContext>
        </DndContext>
    );
}
